import React, { useEffect, useState } from 'react';
import { fetchSurveyByToken, submitSurveyByToken } from '../../api/moduleApi';

const SurveyAccess = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetchSurveyByToken(token);
        if (res.responded) {
          setSubmitted(true);
          setSurvey(null);
        } else {
          setSurvey(res);
        }
      } catch (err) {
        const msg = err.message || 'Failed to load survey';
        if (msg.toLowerCase().includes('expired')) {
          setError('This link has expired.');
        } else {
          setError(msg);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleChange = (qid, value) => {
    setAnswers(prev => ({ ...prev, [qid]: value }));
  };

  const validateMandatory = () => {
    const qs = survey.questions || [];
    const missing = [];
    for (const q of qs) {
      if (q.question_type !== 'Heading' && q.is_mandatory && !answers[q.question_id]) {
        missing.push(q.question_text);
      }
    }
    return missing;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    const missing = validateMandatory();
    if (missing.length > 0) {
      setError(`Please answer all mandatory questions:\n${missing.map(t => `• ${t}`).join('\n')}`);
      return;
    }

    const payload = Object.keys(answers).map(k => ({ question_id: Number(k), value: answers[k] }));
    try {
      await submitSurveyByToken(token, payload);
      setSubmitted(true);
    } catch (err) {
      const msg = err.message || 'Submit failed';
      if (msg.toLowerCase().includes('expired')) {
        setError('This link has expired.');
      } else {
        setError(msg);
      }
    }
  };

  const missing = survey ? validateMandatory() : [];

  if (loading) return <div className="p-6">Loading...</div>;
  if (error) return <div role="alert" aria-live="assertive" className="p-6 text-red-600 whitespace-pre-wrap bg-red-50 border border-red-200 rounded">{error}</div>;
  if (submitted) return <div className="p-6 bg-white rounded shadow">Thank you for completing this survey.</div>;
  if (!survey) return <div className="p-6">No survey available.</div>;

  const { questions, student_email, module_code, module_name, lecturer_name } = survey;
  const hasRatingQuestions = questions && questions.some(q => String(q.question_type || '').trim() === 'Rating');

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto bg-white p-6 rounded shadow">
        <h1 className="text-3xl font-bold text-indigo-700 mb-2">Module Survey</h1>
        
        {module_code && (
          <div className="mb-6 p-4 bg-indigo-50 border-l-4 border-indigo-500 rounded">
            <p className="text-sm font-semibold text-indigo-900">{module_code}</p>
            {module_name && <p className="text-lg font-semibold text-indigo-800">{module_name}</p>}
            {lecturer_name && <p className="text-sm text-indigo-700 mt-1">Lecturer: {lecturer_name}</p>}
          </div>
        )}

        <p className="text-sm text-gray-600 mb-4">Student: {student_email}</p>

        {hasRatingQuestions && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700">
            <span className="font-semibold">Rating key:</span> 1 = Definitely disagree, 5 = Definitely agree
          </div>
        )}

        {error && (
          <div role="alert" aria-live="assertive" className="mb-6 p-4 bg-red-50 border border-red-200 rounded text-red-700 whitespace-pre-wrap text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {questions.map(q => {
            const qType = String(q.question_type || 'Text').trim();
            const qText = String(q.question_text || '').trim();
            const questionTitleId = `question-title-${q.question_id}`;
            const requiredHintId = `question-required-${q.question_id}`;
            const textInputId = `question-text-${q.question_id}`;
            return (
            <div key={q.question_id} className={`p-3 border rounded ${q.is_mandatory && qType !== 'Heading' ? 'border-orange-300 bg-orange-50' : ''}`}>
              <div className="flex items-start gap-2">
                <div className="flex-grow">
                  <div id={questionTitleId} className={`font-semibold ${qType === 'Heading' ? 'text-lg text-indigo-600' : 'text-gray-800'}`}>
                    {qText}
                  </div>
                  {!!q.is_mandatory && qType !== 'Heading' && (
                    <>
                      <span className="text-red-600 font-bold ml-1">*</span>
                      <p id={requiredHintId} className="text-xs text-orange-600 mt-1">(Required)</p>
                    </>
                  )}
                </div>
              </div>

              {qType === 'Rating' && (
                <fieldset className="mt-2" aria-required={q.is_mandatory}>
                  <legend className="sr-only">{qText}</legend>
                  <div className="flex space-x-2" role="radiogroup" aria-labelledby={questionTitleId} aria-describedby={q.is_mandatory ? requiredHintId : undefined}>
                    {Array.from({ length: (q.scale_range ?? q.scale ?? 5) }, (_, i) => i + 1).map(n => (
                      <label key={n} className="inline-flex items-center">
                        <input 
                          name={`q_${q.question_id}`} 
                          type="radio" 
                          value={n} 
                          checked={answers[q.question_id] == n} 
                          onChange={() => handleChange(q.question_id, n)}
                          className="w-4 h-4"
                        />
                        <span className="ml-2">{n}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}

              {qType === 'Text' && (
                <textarea 
                  id={textInputId}
                  value={String(answers[q.question_id] ?? '')} 
                  onChange={(e) => handleChange(q.question_id, e.target.value)} 
                  className={`w-full p-2 border rounded mt-2 ${q.is_mandatory && !answers[q.question_id] ? 'border-red-300' : ''}`}
                  rows={3}
                  aria-labelledby={questionTitleId}
                  aria-describedby={q.is_mandatory ? requiredHintId : undefined}
                  required={q.is_mandatory}
                />
              )}
            </div>
            );
          })}

          <div className="flex gap-2 pt-4">
            <button 
              type="submit" 
              disabled={missing.length > 0}
              className={`px-4 py-2 rounded font-semibold ${missing.length > 0 ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
            >
              Submit Survey
            </button>
            {missing.length > 0 && (
              <span className="text-sm text-orange-600 flex items-center">
                {missing.length} required field{missing.length !== 1 ? 's' : ''} remaining
              </span>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SurveyAccess;
